import { useCallback, useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Boxes, Plus, Pencil, Trash2, DollarSign, ShieldAlert, Wrench, Layers, ScanLine, Printer } from "lucide-react";
import { StatCard } from "./DeskDashboard";
import { AssetScan } from "./AssetScan";
import { AssetLabels } from "./AssetLabels";

const inputCls = "bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none w-full mt-1";
const EMPTY = { name: "", asset_type: "laptop", tag: "", status: "in_use", assigned_to_id: "", location: "", notes: "", value: "", warranty_expiry: "" };
const ST_STYLE = { in_use: "bg-emerald-50 text-emerald-700 border-emerald-200", in_stock: "bg-sky-50 text-sky-700 border-sky-200",
  in_repair: "bg-amber-50 text-amber-700 border-amber-200", retired: "bg-neutral-100 text-neutral-500 border-neutral-200" };
const BAR_COLOR = { in_use: "#10B981", in_stock: "#0D9488", in_repair: "#F59E0B", retired: "#94A3B8" };

const Breakdown = ({ title, icon: Icon, rows, total, testId }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testId}>
    <h3 className="font-semibold font-heading text-sm flex items-center gap-2 mb-4"><Icon size={15} className="text-slate-400" /> {title}</h3>
    {rows.map(([label, count, color]) => (
      <div key={label} className="mb-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="font-medium text-slate-700">{label.replace(/_/g, " ")}</span>
          <span className="text-slate-400">{count} · {total ? Math.round((count / total) * 100) : 0}%</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${total ? (count / total) * 100 : 0}%`, background: color || "var(--brand-accent)" }} />
        </div>
      </div>
    ))}
  </div>
);

export default function Assets() {
  const { user } = useAuth();
  const isAgent = user?.role !== "hd_user";
  const [assets, setAssets] = useState([]);
  const [all, setAll] = useState([]);
  const [members, setMembers] = useState([]);
  const [config, setConfig] = useState(null);
  const [filters, setFilters] = useState({ q: "", status: "", asset_type: "" });
  const [editor, setEditor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showScan, setShowScan] = useState(false);
  const [showLabels, setShowLabels] = useState(false);

  const load = useCallback(() => {
    const p = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    api.get(`/helpdesk/assets?${p}`).then((r) => setAssets(r.data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/helpdesk/assets").then((r) => setAll(r.data)).catch(() => {});
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/helpdesk/company").then((r) => setConfig(r.data)).catch(() => {});
    if (isAgent) api.get("/helpdesk/members").then((r) => setMembers(r.data)).catch(() => {});
  }, [isAgent]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...editor, value: parseFloat(editor.value) || 0 };
      if (editor.id) await api.put(`/helpdesk/assets/${editor.id}`, payload);
      else await api.post("/helpdesk/assets", payload);
      toast.success("Asset saved");
      setEditor(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete asset "${a.name}"?`)) return;
    try { await api.delete(`/helpdesk/assets/${a.id}`); toast.success("Asset deleted"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const soonDate = new Date(Date.now() + 60 * 86400000).toISOString().slice(0, 10);
  const totalValue = all.reduce((s, a) => s + (a.value || 0), 0);
  const wExpired = all.filter((a) => a.warranty_expiry && a.warranty_expiry < today).length;
  const wSoon = all.filter((a) => a.warranty_expiry && a.warranty_expiry >= today && a.warranty_expiry <= soonDate).length;
  const inRepair = all.filter((a) => a.status === "in_repair").length;
  const byStatus = (config?.asset_statuses || []).map((s) => [s, all.filter((a) => a.status === s).length, BAR_COLOR[s]]);
  const byType = (config?.asset_types || []).map((t) => [t, all.filter((a) => a.asset_type === t).length]).filter(([, c]) => c > 0);

  return (
    <div data-testid="desk-assets-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="text-2xl font-bold font-heading flex items-center gap-2"><Boxes size={22} /> Asset Management</h1>
        {isAgent && (
          <div className="flex flex-wrap gap-2">
            <button data-testid="scan-asset-button" onClick={() => setShowScan(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-300 bg-white hover:bg-neutral-50 text-sm font-medium">
              <ScanLine size={15} /> Scan
            </button>
            <button data-testid="print-labels-button" onClick={() => setShowLabels(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-300 bg-white hover:bg-neutral-50 text-sm font-medium">
              <Printer size={15} /> Labels
            </button>
            <button data-testid="new-asset-button" onClick={() => setEditor({ ...EMPTY })}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">
              <Plus size={15} /> New asset
            </button>
          </div>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-5">Track every laptop, server, device and license across your organization.</p>
      {isAgent && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4" data-testid="asset-stats">
            <StatCard icon={Boxes} label="Total assets" value={all.length} tone="slate" testId="asset-stat-total" />
            <StatCard icon={DollarSign} label="Total value" value={`$${totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} tone="emerald" testId="asset-stat-value" />
            <StatCard icon={ShieldAlert} label="Warranty alerts" value={wExpired + wSoon} sub={`${wSoon} soon · ${wExpired} expired`} tone="amber" testId="asset-stat-warranty" />
            <StatCard icon={Wrench} label="In repair" value={inRepair} tone={inRepair > 0 ? "red" : "slate"} testId="asset-stat-repair" />
          </div>
          <div className="grid lg:grid-cols-2 gap-4 mb-5">
            <Breakdown title="By status" icon={Layers} rows={byStatus} total={all.length} testId="asset-by-status" />
            <Breakdown title="By category" icon={Boxes} rows={byType} total={all.length} testId="asset-by-category" />
          </div>
        </>
      )}
      <div className="flex flex-wrap gap-2 mb-4">
        <input data-testid="asset-search" placeholder="Search name or tag..." value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} className={inputCls + " mt-0 w-64"} style={{ width: 240 }} />
        <select data-testid="asset-status-filter" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className={inputCls + " mt-0"} style={{ width: 150 }}>
          <option value="">All statuses</option>
          {(config?.asset_statuses || []).map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select data-testid="asset-type-filter" value={filters.asset_type} onChange={(e) => setFilters((f) => ({ ...f, asset_type: e.target.value }))} className={inputCls + " mt-0"} style={{ width: 170 }}>
          <option value="">All types</option>
          {(config?.asset_types || []).map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
      </div>
      <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
        {assets.length === 0 ? <p className="p-6 text-sm text-neutral-400" data-testid="assets-empty">No assets{isAgent ? " — add your first laptop, server or license." : " assigned to you."}</p> : (
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-neutral-200 text-sm text-left">
            <thead><tr className="bg-neutral-50">
              {["Asset", "Type", "Tag", "Status", "Assigned to", "Location", "Value", ""].map((h, i) => <th key={i} className="px-5 py-3 font-medium text-neutral-500 uppercase tracking-wider text-xs">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-neutral-100">
              {assets.map((a) => (
                <tr key={a.id} data-testid={`asset-row-${a.id}`} className="hover:bg-neutral-50">
                  <td className="px-5 py-3 font-medium">{a.name}</td>
                  <td className="px-5 py-3 text-neutral-500">{a.asset_type.replace(/_/g, " ")}</td>
                  <td className="px-5 py-3 font-mono text-xs">{a.tag || "—"}</td>
                  <td className="px-5 py-3"><span className={`text-xs font-medium border rounded-full px-2.5 py-0.5 ${ST_STYLE[a.status]}`}>{a.status.replace(/_/g, " ")}</span></td>
                  <td className="px-5 py-3 text-neutral-500">{a.assigned_to_name || "—"}</td>
                  <td className="px-5 py-3 text-neutral-500">{a.location || "—"}</td>
                  <td className="px-5 py-3 text-neutral-500">{a.value ? `$${a.value.toLocaleString()}` : "—"}</td>
                  <td className="px-5 py-3">
                    {isAgent && (
                      <div className="flex gap-1.5">
                        <button data-testid={`asset-edit-${a.id}`} onClick={() => setEditor({ ...EMPTY, ...a, assigned_to_id: a.assigned_to_id || "" })}
                          className="p-1.5 border border-neutral-300 rounded-md hover:bg-neutral-50"><Pencil size={12} /></button>
                        <button data-testid={`asset-delete-${a.id}`} onClick={() => remove(a)}
                          className="p-1.5 border border-red-200 text-red-600 rounded-md hover:bg-red-50"><Trash2 size={12} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {showScan && <AssetScan members={members} onClose={() => setShowScan(false)} onChanged={load} />}
      {showLabels && <AssetLabels assets={all} onClose={() => setShowLabels(false)} onChanged={load} />}

      {editor && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4" data-testid="asset-editor-modal">
          <form onSubmit={save} className="bg-white rounded-2xl border border-neutral-200 shadow-xl max-w-md w-full p-7 max-h-[85vh] overflow-y-auto">
            <h3 className="font-semibold font-heading text-lg mb-4">{editor.id ? "Edit asset" : "New asset"}</h3>
            <label className="text-sm font-medium text-neutral-700">Name</label>
            <input data-testid="asset-name-input" required minLength={2} value={editor.name} onChange={(e) => setEditor((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
            <div className="grid grid-cols-2 gap-2 mt-2">
              <label className="text-xs text-neutral-500">Type
                <select data-testid="asset-type-input" value={editor.asset_type} onChange={(e) => setEditor((f) => ({ ...f, asset_type: e.target.value }))} className={inputCls}>
                  {(config?.asset_types || []).map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select></label>
              <label className="text-xs text-neutral-500">Status
                <select data-testid="asset-status-input" value={editor.status} onChange={(e) => setEditor((f) => ({ ...f, status: e.target.value }))} className={inputCls}>
                  {(config?.asset_statuses || []).map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select></label>
              <label className="text-xs text-neutral-500">Serial / asset tag
                <input data-testid="asset-tag-input" value={editor.tag} onChange={(e) => setEditor((f) => ({ ...f, tag: e.target.value }))} className={inputCls} /></label>
              <label className="text-xs text-neutral-500">Location
                <input data-testid="asset-location-input" value={editor.location} onChange={(e) => setEditor((f) => ({ ...f, location: e.target.value }))} className={inputCls} /></label>
              <label className="text-xs text-neutral-500">Value ($)
                <input data-testid="asset-value-input" type="number" min="0" step="0.01" value={editor.value ?? ""} onChange={(e) => setEditor((f) => ({ ...f, value: e.target.value }))} className={inputCls} /></label>
              <label className="text-xs text-neutral-500">Warranty expiry
                <input data-testid="asset-warranty-input" type="date" value={editor.warranty_expiry || ""} onChange={(e) => setEditor((f) => ({ ...f, warranty_expiry: e.target.value }))} className={inputCls} /></label>
            </div>
            <label className="text-xs text-neutral-500 block mt-2">Assigned to
              <select data-testid="asset-assigned-input" value={editor.assigned_to_id} onChange={(e) => setEditor((f) => ({ ...f, assigned_to_id: e.target.value }))} className={inputCls}>
                <option value="">Unassigned</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select></label>
            <label className="text-xs text-neutral-500 block mt-2">Notes
              <textarea data-testid="asset-notes-input" rows={2} value={editor.notes} onChange={(e) => setEditor((f) => ({ ...f, notes: e.target.value }))} className={inputCls} /></label>
            <div className="flex gap-2 mt-5">
              <button data-testid="asset-save-button" disabled={busy} className="flex-1 py-2.5 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">{busy ? "Saving..." : "Save asset"}</button>
              <button type="button" onClick={() => setEditor(null)} className="px-5 py-2.5 rounded-md border border-neutral-300 text-sm font-medium hover:bg-neutral-50">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
